/**
 * AP1 (#3/#2) — „aus Prozess erstellen" für die Aufgaben-Liste. Aufgaben
 * entstehen NICHT durch Direkt-Create, sondern durch Materialisierung eines
 * on-demand-Prozesses (D-147/D-150). Dieser Dialog:
 *   1. listet die on-demand-Prozesse (optional auf die aktive Gruppe verengt),
 *   2. lädt beim Auswählen die konfigurierten Pflicht-Inputs (required_inputs),
 *   3. materialisiert — L1 → Serie, sonst Einzel-Task — und stempelt die Gruppe
 *      serverseitig über den Prozess.
 * Bewusst eigenständig (kein Umbau von RyfeGroup); die API-Methoden sind geteilt.
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";

import type { ProcessRequiredInput } from "../api";

type Proc = { id: string; name: string; level: number | null };

export const ProcessInstantiateDialog = ({
  wsId,
  groupId,
  onClose,
  onDone,
}: {
  wsId: string;
  groupId: string | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [procs, setProcs] = useState<Proc[] | null>(null);
  const [proc, setProc] = useState<Proc | null>(null);
  const [inputs, setInputs] = useState<ProcessRequiredInput[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Alle Prozesse des aktiven Teams zur Auswahl (nicht nur on_demand — das war
    // zu eng und ließ den Dropdown leer, wenn Prozesse anders typisiert waren).
    // `groupId=null` (Aufgaben-Liste ohne aktive Gruppe) liefert die WS-weiten.
    ryfeApi
      .listProcesses(wsId, { groupId })
      .then((r) => {
        if (!cancelled) {
          setProcs(r.processes);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError((e as Error).message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [wsId, groupId]);

  const materialize = async (p: Proc, body: Record<string, string>) => {
    setBusy(true);
    setError(null);
    try {
      if (p.level === 1) {
        await ryfeApi.materializeProcessSeries(wsId, p.id, body);
      } else {
        await ryfeApi.materializeProcess(wsId, p.id, body);
      }
      onDone();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const pick = async (p: Proc) => {
    setError(null);
    try {
      const detail = await ryfeApi.getProcess(wsId, p.id);
      const ri = detail.required_inputs || [];
      if (ri.length === 0) {
        void materialize(p, {});
        return;
      }
      setProc(p);
      setInputs(ri);
      setValues({});
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Aufgabe aus Prozess"
      onClick={onClose}
    >
      <form
        className="ryfe-modal__card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (proc) {
            void materialize(proc, values);
          }
        }}
      >
        <h2 className="ryfe-panel__title">Aufgabe aus Prozess</h2>

        {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

        {/* Schritt 1 — Prozess wählen. */}
        {!proc && (
          <>
            {procs && procs.length === 0 && (
              <p className="ryfe-muted" style={{ padding: 0 }}>
                Keine Prozesse verfügbar. Lege zuerst im Prozesse-Systemlisten-
                Editor einen Prozess an.
              </p>
            )}
            <div className="ryfe-list-links">
              {(procs || []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="ryfe-linkish"
                  onClick={() => void pick(p)}
                >
                  {p.name}
                  {p.level === 1 ? " · L1 (Serie)" : ""}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Schritt 2 — Pflicht-Inputs. */}
        {proc && (
          <>
            <div className="ryfe-muted" style={{ padding: 0 }}>
              {proc.name} — Pflichtangaben für die Instanziierung.
            </div>
            {inputs.map((ri) => (
              <div key={ri.field}>
                <div className="ryfe-field-label">{ri.label || ri.field}</div>
                {ri.field === "description" ? (
                  <textarea
                    className="ryfe-field-input"
                    value={values[ri.field] || ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [ri.field]: e.target.value }))
                    }
                  />
                ) : (
                  <input
                    className="ryfe-field-input"
                    type={ri.field === "due_date" ? "date" : "text"}
                    placeholder={ri.contact_type || undefined}
                    value={values[ri.field] || ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [ri.field]: e.target.value }))
                    }
                  />
                )}
              </div>
            ))}
          </>
        )}

        <div className="ryfe-modal__actions">
          <button type="button" className="ryfe-btn" onClick={onClose}>
            Abbrechen
          </button>
          <span style={{ flex: 1 }} />
          {proc && (
            <button
              type="submit"
              className="ryfe-btn ryfe-btn--primary"
              disabled={
                busy || inputs.some((ri) => !(values[ri.field] || "").trim())
              }
            >
              {busy ? "…" : "Aufgabe erstellen"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
